import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from '../components/UI';

export default function AdminDashboardScreen({ user, navigate, showToast }) {
    const [activeTab, setActiveTab] = useState('stats'); // 'stats', 'users', 'children'
    const [users, setUsers] = useState([]);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [userPage, setUserPage] = useState(1);
    const [childPage, setChildPage] = useState(1);
    const itemsPerPage = 10;

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch both collections once for stats
            const [usersSnapshot, childrenSnapshot] = await Promise.all([
                getDocs(collection(db, 'users')),
                getDocs(collection(db, 'children'))
            ]);
            
            setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setChildren(childrenSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error fetching admin data:", error);
            showToast("Gagal mengambil data", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Pagination Calculations
    const totalUserPages = Math.ceil(users.length / itemsPerPage);
    const totalChildPages = Math.ceil(children.length / itemsPerPage);

    const paginatedUsers = users.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);
    const paginatedChildren = children.slice((childPage - 1) * itemsPerPage, childPage * itemsPerPage);

    // Stats Calculations
    const examinedChildren = children.filter(c => c.lastAssessment && c.lastAssessment.overallStatus);
    const normalCount = examinedChildren.filter(c => ['Sesuai', 'Normal'].includes(c.lastAssessment.overallStatus)).length;
    const meragukanCount = examinedChildren.filter(c => c.lastAssessment.overallStatus === 'Meragukan').length;
    const penyimpanganCount = examinedChildren.filter(c => ['Ada Penyimpangan', 'Penyimpangan'].includes(c.lastAssessment.overallStatus)).length;
    const rujukanCount = examinedChildren.filter(c => c.lastAssessment.overallStatus === 'Perlu Rujukan Segera').length;


    const handleToggleVerification = async (userId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await updateDoc(doc(db, 'users', userId), { isVerified: newStatus });
            setUsers(users.map(u => u.id === userId ? { ...u, isVerified: newStatus } : u));
            showToast(newStatus ? 'Pengguna diverifikasi' : 'Verifikasi dicabut', 'success');
        } catch (error) {
            console.error("Error updating verification:", error);
            showToast("Gagal memperbarui verifikasi", "error");
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Yakin ingin menghapus dokumen pengguna ini?")) return;
        try {
            await deleteDoc(doc(db, 'users', userId));
            setUsers(users.filter(u => u.id !== userId));
            showToast("Pengguna dihapus", "success");
        } catch (error) {
            console.error("Error deleting user:", error);
            showToast("Gagal menghapus pengguna", "error");
        }
    };

    const handleDeleteChild = async (childId) => {
        if (!window.confirm("Yakin ingin menghapus seluruh data anak ini?")) return;
        try {
            await deleteDoc(doc(db, 'children', childId));
            setChildren(children.filter(c => c.id !== childId));
            showToast("Data anak dihapus", "success");
        } catch (error) {
            console.error("Error deleting child:", error);
            showToast("Gagal menghapus anak", "error");
        }
    };

    return (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 animate-fade-in-up mb-24">
            <h2 className="text-2xl font-extrabold text-gray-800 mb-6 flex items-center space-x-2">
                <span className="text-3xl">👑</span>
                <span>Dashboard Admin</span>
            </h2>
            <div className="flex flex-wrap gap-3 mb-6">
                <button 
                    onClick={() => setActiveTab('stats')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'stats' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    📊 Statistik
                </button>
                <button 
                    onClick={() => setActiveTab('users')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'users' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    👨‍👩‍👧‍👦 Kelola Pengguna
                </button>
                <button 
                    onClick={() => setActiveTab('children')} 
                    className={`px-4 py-2 font-bold rounded-xl transition ${activeTab === 'children' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    👶 Kelola Data Anak
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center p-10"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div></div>
            ) : (
                <div className="space-y-4">
                    {activeTab === 'stats' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-blue-600 text-sm font-bold uppercase tracking-wider mb-1">Total Pengguna</p>
                                <p className="text-3xl font-extrabold text-blue-900">{users.length}</p>
                            </div>
                            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-purple-600 text-sm font-bold uppercase tracking-wider mb-1">Total Anak</p>
                                <p className="text-3xl font-extrabold text-purple-900">{children.length}</p>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-indigo-600 text-sm font-bold uppercase tracking-wider mb-1">Anak Diperiksa</p>
                                <p className="text-3xl font-extrabold text-indigo-900">{examinedChildren.length}</p>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-emerald-600 text-sm font-bold uppercase tracking-wider mb-1">Status Normal</p>
                                <p className="text-3xl font-extrabold text-emerald-900">{normalCount}</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-amber-600 text-sm font-bold uppercase tracking-wider mb-1">Meragukan</p>
                                <p className="text-3xl font-extrabold text-amber-900">{meragukanCount}</p>
                            </div>
                            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5 shadow-sm">
                                <p className="text-rose-600 text-sm font-bold uppercase tracking-wider mb-1">Penyimpangan & Rujukan</p>
                                <p className="text-3xl font-extrabold text-rose-900">{penyimpanganCount + rujukanCount}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="space-y-4 animate-fade-in">
                            {paginatedUsers.map(u => (
                        <div key={u.id} className="p-4 border rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 hover:bg-gray-50 transition">
                            <div>
                                <h3 className="font-bold text-gray-800">{u.username || u.email || 'Tanpa Nama'}</h3>
                                <p className="text-xs text-gray-500">{u.email || '-'}</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-[10px] rounded uppercase font-bold">{u.role || 'Unknown'}</span>
                                    {(u.role === 'Guru PAUD' || u.role === 'Tenaga Kesehatan') && (
                                        <span className={`px-2 py-1 text-[10px] rounded uppercase font-bold ${u.isVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {u.isVerified ? 'Terverifikasi' : 'Belum Diverifikasi'}
                                        </span>
                                    )}
                                </div>
                                {u.institutionName && <p className="text-[11px] mt-1.5 font-medium text-gray-600">🏢 {u.institutionName}</p>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(u.role === 'Guru PAUD' || u.role === 'Tenaga Kesehatan') && (
                                    <button 
                                        onClick={() => handleToggleVerification(u.id, u.isVerified)}
                                        className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${u.isVerified ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                                    >
                                        {u.isVerified ? 'Cabut Verifikasi' : 'Verifikasi'}
                                    </button>
                                )}
                                <button onClick={() => handleDeleteUser(u.id)} className="px-3 py-1.5 bg-red-100 text-red-600 text-[11px] font-bold rounded-lg hover:bg-red-200 transition-colors">
                                    🗑️ Hapus
                                </button>
                            </div>
                        </div>
                    ))}
                            {/* Pagination Controls for Users */}
                            {totalUserPages > 1 && (
                                <div className="flex items-center justify-between mt-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
                                    <button 
                                        disabled={userPage === 1} 
                                        onClick={() => setUserPage(userPage - 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Sebelumnya
                                    </button>
                                    <span className="text-sm font-bold text-gray-600">Halaman {userPage} dari {totalUserPages}</span>
                                    <button 
                                        disabled={userPage === totalUserPages} 
                                        onClick={() => setUserPage(userPage + 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Selanjutnya
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'children' && (
                        <div className="space-y-4 animate-fade-in">
                            {paginatedChildren.map(c => (
                        <div key={c.id} className="p-4 border rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 hover:bg-gray-50 transition">
                            <div>
                                <h3 className="font-bold text-gray-800">{c.name || 'Tanpa Nama'}</h3>
                                <p className="text-xs text-gray-500 font-mono mt-0.5">NIK: {c.nik || 'Tidak ada'}</p>
                                {c.schoolName && <p className="text-[11px] mt-1 text-gray-600">🏫 {c.schoolName}</p>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => navigate('viewChild', c)} className="px-3 py-1.5 bg-blue-100 text-blue-700 text-[11px] font-bold rounded-lg hover:bg-blue-200 transition-colors">
                                    👁️ Lihat
                                </button>
                                <button onClick={() => navigate('editChild', c)} className="px-3 py-1.5 bg-purple-100 text-purple-700 text-[11px] font-bold rounded-lg hover:bg-purple-200 transition-colors">
                                    ✏️ Edit
                                </button>
                                <button onClick={() => handleDeleteChild(c.id)} className="px-3 py-1.5 bg-red-100 text-red-600 text-[11px] font-bold rounded-lg hover:bg-red-200 transition-colors">
                                    🗑️ Hapus
                                </button>
                            </div>
                        </div>
                    ))}
                            {/* Pagination Controls for Children */}
                            {totalChildPages > 1 && (
                                <div className="flex items-center justify-between mt-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
                                    <button 
                                        disabled={childPage === 1} 
                                        onClick={() => setChildPage(childPage - 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Sebelumnya
                                    </button>
                                    <span className="text-sm font-bold text-gray-600">Halaman {childPage} dari {totalChildPages}</span>
                                    <button 
                                        disabled={childPage === totalChildPages} 
                                        onClick={() => setChildPage(childPage + 1)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-bold disabled:opacity-50"
                                    >
                                        Selanjutnya
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeTab === 'users' && users.length === 0 && !loading && (
                        <p className="text-center text-gray-500 py-10">Tidak ada data pengguna.</p>
                    )}
                    {activeTab === 'children' && children.length === 0 && !loading && (
                        <p className="text-center text-gray-500 py-10">Tidak ada data anak.</p>
                    )}
                </div>
            )}
        </div>
    );
}
